import * as React from "react";
import { LEVELS } from "@/constants";
import { Box, Button, Card, IconButton, Stack, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { CopySimple } from "@phosphor-icons/react/dist/ssr/CopySimple";

import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { systemLogsKeys } from "@/hooks/api/use-system-logs";
import { CustomTable } from "@/components/customTable";
import { Column, ExpandableRowConfig, FilterOption, FilterType } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";
import { formatDate } from "@/utils";

export function SystemLogs(): React.JSX.Element {
	const {
		items: systemLogs,
		isLoading,
		totalItems,
		refetchData: fetchSystemLogs,
	} = useCachedTableStateWithUrl({
		queryKey: systemLogsKeys.lists(),
		endpoint: '/system-logs',
	});

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		return [
			{
				name: "levels",
				label: "Levels",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: LEVELS,
			},
			{
				name: "services",
				label: "Services",
				value: [],
				type: FilterType.MULTI_SELECT_FREE_SOLO,
			},
			{
				name: "threads",
				label: "Threads",
				value: [],
				type: FilterType.MULTI_SELECT_FREE_SOLO,
			},
			{
				name: "timestampFrom",
				label: "From Date",
				value: "",
				type: FilterType.TIMESTAMP,
			},
			{
				name: "timestampTo",
				label: "To Date",
				value: "",
				type: FilterType.TIMESTAMP,
			},
		];
	}, []);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
			},
			{
				header: {
					id: "timestamp",
					label: "Date",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "timestamp",
				getCellValue: (row: any) => formatDate(row.timestamp),
			},

			{
				header: {
					id: "level",
					label: "Level",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "level",
				getCellValue: (row: any) => `${row.level}`,
			},
			{
				header: {
					id: "service",
					label: "Service",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "service",
				getCellValue: (row: any) => `${row.service}`,
			},

			{
				header: {
					id: "threads",
					label: "Threads",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "threads",
				getCellValue: (row: any) => {
					const entries: any = row.thread && Object.entries(row.thread);

					return <span>{row.thread ? entries[0][1] : ""}</span>;
				},
			},
			{
				header: {
					id: "message",
					label: "Message",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "message",
				getCellValue: (row: any) => `${row.message}`,
			},
		];
	}, []);

	const expandableRowConfig: ExpandableRowConfig = {
		enabled: true,
		renderExpandedContent: (record) => {
			const clickableArrayFields = [
				{
					label: "Integration UUID",
					value: record.integrationUuid,
					filterKey: "integrationId",
				},
			];

			const clickableFields = [
				{ label: "Agent ID", value: record.agentId, filterKey: "agentId" },
				{
					label: "Conversation ID",
					value: record.conversationId,
					filterKey: "conversationId",
				},
				{
					label: "Customer ID",
					value: record.customerId,
					filterKey: "customerId",
				},
				{
					label: "Interaction ID",
					value: record.interactionId,
					filterKey: "interactionId",
				},
			];

			const nonClickableFields = [{ label: "Message", value: record.message }];

			const renderField = ({ label, value, filterKey }) => (
				<div
					key={label}
					onClick={(e) => {
						e.stopPropagation();
					}}
					style={{
						fontSize: "12px",
					}}
				>
					<strong>{label}: </strong>
					<span
						style={{
							maxWidth: 400,
							cursor: filterKey ? "pointer" : "default",
							color: filterKey ? "#1890ff" : "inherit",
							textDecoration: filterKey ? "underline" : "none",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
						}}
					>
						{value}
					</span>
				</div>
			);

			const renderArrayField = ({ label, value, filterKey }) => (
				<div
					key={label}
					onClick={(e) => {
						e.stopPropagation();
					}}
					style={{
						fontSize: "12px",
					}}
				>
					<strong>{label}: </strong>
					<span
						style={{
							maxWidth: 400,
							cursor: filterKey ? "pointer" : "default",
							color: filterKey ? "#1890ff" : "inherit",
							textDecoration: filterKey ? "underline" : "none",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
						}}
					>
						{value}
					</span>
				</div>
			);

			return (
				<Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: 2 }}>
					{nonClickableFields
						.filter(({ value }) => value)
						.map(({ label, value }) => renderField({ label, value, filterKey: undefined }))}
					<div>
						<span
							style={{
								fontWeight: "500",
								fontSize: "12px",
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
							}}
						>
							<span style={{ fontWeight: "bold" }}>Params: </span>
							{JSON.stringify(record.params, null, Infinity)}
						</span>
					</div>

					<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
						{clickableArrayFields
							.filter(({ value }) => value)
							.map(({ label, value, filterKey }) => renderArrayField({ label, value, filterKey }))}
						{clickableFields
							.filter(({ value }) => value)
							.map(({ label, value, filterKey }) => renderField({ label, value, filterKey }))}
					</div>

					<Tooltip
						title={
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<span style={{ flex: 1 }}>{record.stack}</span>
								<IconButton
									size="small"
									onClick={() => navigator.clipboard.writeText(record.stack)}
									color="primary"
									sx={{ backgroundColor: "white" }}
								>
									<CopySimple />
								</IconButton>
							</div>
						}
						slotProps={{
							tooltip: {
								sx: {
									maxWidth: "80%",
									whiteSpace: "normal",
								},
							},
						}}
					>
						<Button variant="contained" color="primary" sx={{ maxWidth: "max-content" }} size="small">
							View Stack
						</Button>
					</Tooltip>
				</Box>
			);
		},
	};

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">System Logs</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								View system logs
							</Typography>
						</Box>
					</Stack>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={systemLogs}
								refetchData={fetchSystemLogs}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
								expandableRow={expandableRowConfig}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
